import { useEffect, useState } from "react";
import axios from "axios";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Wallet, Phone, User, Calendar, CheckCircle, Zap, Clock, XCircle, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export const AdminMeruRequests = () => {
    const [requests, setRequests] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null);

    useEffect(() => {
        fetchRequests();
    }, []);

    const fetchRequests = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API}/meru/admin/requests`);
            setRequests(response.data.requests);
            setStats(response.data.stats);
        } catch (error) {
            console.error("Error fetching Meru requests:", error);
            toast.error("Pa kapab chaje demann Meru yo.");
        } finally {
            setLoading(false);
        }
    };

    const confirmPayment = async (requestId) => {
        try {
            setActionLoading(requestId);
            await axios.post(`${API}/meru/admin/requests/${requestId}/confirm-payment`);
            toast.success("Peman konfime!");
            fetchRequests();
        } catch (error) {
            toast.error("Erè nan konfime peman.");
        } finally {
            setActionLoading(null);
        }
    };

    const markRecharged = async (requestId) => {
        const ref = prompt("Antre referans transaksyon Meru a (opsyonèl):");
        try {
            setActionLoading(requestId);
            await axios.post(`${API}/meru/admin/requests/${requestId}/mark-recharged`, { meru_ref: ref });
            toast.success("Kat Meru make kòm rechaje!");
            fetchRequests();
        } catch (error) {
            toast.error("Erè nan make kòm rechaje.");
        } finally {
            setActionLoading(null);
        }
    };

    const cancelRequest = async (requestId) => {
        if (!confirm("Èske ou sèten ou vle anile demann sa a?")) return;
        const reason = prompt("Poukisa w ap anile l?");
        try {
            setActionLoading(requestId);
            await axios.post(`${API}/meru/admin/requests/${requestId}/cancel`, { reason });
            toast.success("Demann anile!");
            fetchRequests();
        } catch (error) {
            toast.error("Erè nan anile demann.");
        } finally {
            setActionLoading(null);
        }
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('fr-HT', {
            style: 'decimal',
            minimumFractionDigits: 0,
        }).format(price);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        return new Date(dateStr).toLocaleDateString('fr-HT', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        const st = {
            "pending_payment": { label: "Atant Peman", class: "bg-amber-500 text-white", icon: Clock },
            "payment_received": { label: "Peye - Pou Recharge", class: "bg-blue-600 text-white shadow-lg shadow-blue-200", icon: Zap },
            "completed": { label: "Konplete", class: "bg-green-600 text-white", icon: CheckCircle },
            "cancelled": { label: "Anile", class: "bg-red-500 text-white", icon: XCircle }
        };
        const config = st[status] || { label: status, class: "bg-gray-500 text-white", icon: AlertCircle };
        return (
            <span className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 ${config.class}`}>
                <config.icon className="w-3 h-3" />
                {config.label}
            </span>
        );
    };

    return (
        <AdminLayout>
            <div className="space-y-8">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-black text-[#232F3E] tracking-tighter italic uppercase leading-none">
                            Rechaj <span className="text-green-600">Meru Card</span>
                        </h1>
                        <p className="text-gray-400 font-bold uppercase tracking-widest text-[9px] mt-2">
                            Jere tout demann rechaj kat Meru kliyan yo.
                        </p>
                    </div>
                    {stats && (
                        <div className="flex gap-4">
                            <div className="bg-white px-6 py-4 rounded-2xl shadow-sm border border-gray-100 text-center">
                                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Atant</p>
                                <p className="text-2xl font-black text-blue-600">{stats.payment_received}</p>
                            </div>
                            <div className="bg-white px-6 py-4 rounded-2xl shadow-sm border border-gray-100 text-center">
                                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Total USD</p>
                                <p className="text-2xl font-black text-green-600">${stats.total_usd_recharged}</p>
                            </div>
                        </div>
                    )}
                </div>

                {loading ? (
                    <div className="flex justify-center py-24">
                        <div className="w-12 h-12 border-4 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                ) : (
                    <Card className="border-0 shadow-2xl rounded-[35px] overflow-hidden bg-white">
                        <CardContent className="p-0">
                            {requests.length > 0 ? (
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="bg-gray-50/50 text-[10px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100">
                                                <th className="py-6 px-10 text-left">Kliyan / Nimewo</th>
                                                <th className="py-6 px-8 text-left">Meru Info</th>
                                                <th className="py-6 px-8 text-left">Kantite USD</th>
                                                <th className="py-6 px-8 text-left">Total HTG</th>
                                                <th className="py-6 px-8 text-left">Estati</th>
                                                <th className="py-6 px-8 text-left">Dat</th>
                                                <th className="py-6 px-8 text-center text-[#FF9900]">Aksyon</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-50">
                                            {requests.map((req) => (
                                                <tr key={req.id} className="hover:bg-gray-50/80 transition-all border-b border-gray-50 last:border-0">
                                                    <td className="py-6 px-10">
                                                        <div className="flex flex-col">
                                                            <span className="font-black text-[#232F3E] text-sm tracking-tight">{req.client_name}</span>
                                                            <span className="text-[10px] text-gray-500 font-bold">{req.client_phone}</span>
                                                            <span className="text-[9px] text-gray-400 font-bold uppercase tracking-widest mt-1">REF: {req.request_number}</span>
                                                        </div>
                                                    </td>
                                                    <td className="py-6 px-8">
                                                        <div className="flex flex-col">
                                                            <span className="text-xs font-bold text-gray-700">{req.meru_email || req.meru_phone || "BEZWEEN KREYE"}</span>
                                                            {req.needs_account_creation && (
                                                                <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-md text-[9px] font-black uppercase w-fit mt-1">Nouvo Kont</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="py-6 px-8">
                                                        <span className="font-black text-lg text-[#232F3E]">${req.amount_usd.toFixed(2)}</span>
                                                    </td>
                                                    <td className="py-6 px-8">
                                                        <div className="flex flex-col">
                                                            <span className="font-black text-emerald-600">{formatPrice(req.total_htg)} HTG</span>
                                                            <span className="text-[9px] text-gray-400">@ {req.exchange_rate}</span>
                                                        </div>
                                                    </td>
                                                    <td className="py-6 px-8">
                                                        {getStatusBadge(req.status)}
                                                    </td>
                                                    <td className="py-6 px-8 text-[11px] font-bold text-gray-400 uppercase">
                                                        {formatDate(req.created_at)}
                                                    </td>
                                                    <td className="py-6 px-8">
                                                        <div className="flex items-center justify-center gap-2">
                                                            {req.status === "pending_payment" && (
                                                                <Button 
                                                                    size="sm" 
                                                                    onClick={() => confirmPayment(req.id)}
                                                                    className="bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-black uppercase rounded-xl h-9 px-4"
                                                                    disabled={actionLoading === req.id}
                                                                >
                                                                    Konfime Peman
                                                                </Button>
                                                            )}
                                                            {req.status === "payment_received" && (
                                                                <Button 
                                                                    size="sm" 
                                                                    onClick={() => markRecharged(req.id)}
                                                                    className="bg-green-600 hover:bg-green-700 text-white text-[10px] font-black uppercase rounded-xl h-9 px-4 shadow-lg shadow-green-100"
                                                                    disabled={actionLoading === req.id}
                                                                >
                                                                    <Zap className="w-3 h-3 mr-1" /> Make Rechaje
                                                                </Button>
                                                            )}
                                                            {req.status !== "completed" && req.status !== "cancelled" && (
                                                                <Button 
                                                                    size="sm" 
                                                                    variant="ghost"
                                                                    onClick={() => cancelRequest(req.id)}
                                                                    className="text-red-400 hover:text-red-600 hover:bg-red-50 text-[10px] font-black uppercase h-9 w-9 p-0 rounded-xl"
                                                                    disabled={actionLoading === req.id}
                                                                >
                                                                    <XCircle className="w-4 h-4" />
                                                                </Button>
                                                            )}
                                                            {req.status === "completed" && (
                                                                <div className="text-[10px] font-black text-green-600 uppercase">
                                                                    Rechaje ✅
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center py-32 text-gray-400">
                                    <Wallet className="w-20 h-20 mx-auto mb-6 opacity-10" />
                                    <p className="font-black uppercase tracking-widest text-[11px]">Pa gen okenn demann rechaj Meru ankò.</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}
            </div>
        </AdminLayout>
    );
};
